import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useState } from "react";
import { useOrganization } from "@/hooks/useOrganization";
import { useAuth } from "@/hooks/useAuth";
import { Users, Loader2 } from "lucide-react";
import LevelBadge from "@/components/level/LevelBadge";

interface StaffRole {
  id: string;
  name: string;
  color: string | null;
}

interface StaffAssignment {
  user_id: string;
  role_id: string;
}

// Staff-only roles (no "player" — players don't appear on this page)
const STAFF_ROLE_OPTIONS = [
  { value: "coach", labelKey: "usersPage.roleCoach" },
  { value: "admin", labelKey: "usersPage.roleAdmin" },
];

export default function AdminUsers() {
  const { t } = useTranslation("admin");
  const qc = useQueryClient();
  const { organization } = useOrganization();
  const { user: adminUser } = useAuth();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [levelDialog, setLevelDialog] = useState<{ id: string; name: string; currentLevel: number; newLevel: number } | null>(null);
  const [levelReason, setLevelReason] = useState("");
  const pageSize = 20;

  // Step 1: fetch staff user IDs (non-player roles)
  const { data: staffUserIds = [] } = useQuery({
    queryKey: ["admin-staff-user-ids"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("user_id")
        .neq("role", "player")
        .eq("organization_id", organization!.id);
      if (error) throw error;
      return (data ?? []).map((r) => r.user_id);
    },
  });

  // Step 2: fetch profiles for staff only
  const { data: profiles = [], isLoading } = useQuery({
    queryKey: ["admin-users", search, page, staffUserIds],
    queryFn: async () => {
      if (staffUserIds.length === 0) return [];
      // INTENTIONAL: bucket-C admin-PII read via "Admin all profiles" RLS policy.
      // Admin staff management page — needs full profile incl. phone for search.
      // FOLLOW-UP: narrow SELECT * to explicit allow-list per the
      // `tasks/lessons.md` "Forbid .from('profiles').select('*')" lesson.
      let query = supabase
        .from("profiles")
        .select("*")
        .in("id", staffUserIds)
        .order("created_at", { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);
      if (search) {
        query = query.or(`full_name.ilike.%${search}%,phone.ilike.%${search}%`);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    enabled: staffUserIds.length > 0,
  });

  const { data: userRoles = [] } = useQuery({
    queryKey: ["admin-user-roles"],
    queryFn: async () => {
      const { data, error } = await supabase.from("user_roles").select("*").eq("organization_id", organization!.id);
      if (error) throw error;
      return data ?? [];
    },
  });

  const updateLevel = useMutation({
    mutationFn: async ({ id, oldLevel, newLevel, reason }: { id: string; oldLevel: number; newLevel: number; reason: string }) => {
      // 1. Update profile
      // INTENTIONAL: bucket-C admin-PII write via "Admin all profiles" RLS policy.
      // Admin level-override UI; stays on public.profiles.
      const { error } = await supabase.from("profiles").update({
        level: newLevel,
        level_source: "admin_override",
        level_assessed_at: new Date().toISOString(),
      } as any).eq("id", id);
      if (error) throw error;

      // 2. Write audit log
      await supabase.from("level_change_log" as any).insert({
        user_id: id,
        organization_id: organization?.id,
        old_level: oldLevel,
        new_level: newLevel,
        changed_by: adminUser?.id,
        change_source: "admin_override",
        reason: reason || null,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success(t("usersPage.levelUpdated"));
      setLevelDialog(null);
      setLevelReason("");
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("common.couldNotUpdate")); },
  });

  const toggleRole = useMutation({
    mutationFn: async ({ userId, role, add }: { userId: string; role: string; add: boolean }) => {
      if (add) {
        const { error } = await supabase.from("user_roles").insert({ user_id: userId, role, organization_id: organization!.id });
        if (error) throw error;
      } else {
        const { error } = await supabase.from("user_roles").delete().eq("user_id", userId).eq("role", role).eq("organization_id", organization!.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-user-roles"] });
      qc.invalidateQueries({ queryKey: ["admin-staff-user-ids"] });
      qc.invalidateQueries({ queryKey: ["user-roles"] });
      toast.success(t("usersPage.roleUpdated"));
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("usersPage.couldNotChangeRole")); },
  });

  // Staff roles
  const { data: staffRoles = [] } = useQuery({
    queryKey: ["staff-roles-list", organization?.id],
    queryFn: async () => {
      const { data } = await supabase.from("staff_roles" as any).select("id, name, color").eq("organization_id", organization!.id).order("sort_order");
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  const { data: staffAssignments = [] } = useQuery({
    queryKey: ["staff-assignments-list", organization?.id],
    queryFn: async () => {
      const { data } = await supabase.from("staff_assignments" as any).select("user_id, role_id").eq("organization_id", organization!.id);
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  const assignStaffRole = useMutation({
    mutationFn: async ({ userId, roleId }: { userId: string; roleId: string | null }) => {
      if (!roleId) {
        await supabase.from("staff_assignments" as any).delete().eq("user_id", userId).eq("organization_id", organization!.id);
      } else {
        const { error } = await supabase.from("staff_assignments" as any).upsert({
          organization_id: organization!.id,
          user_id: userId,
          role_id: roleId,
        }, { onConflict: "organization_id,user_id" });
        if (error) throw error;
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["staff-assignments-list"] }); toast.success(t("usersPage.staffRoleUpdated")); },
    onError: (err: Error) => toast.error(err?.message || t("common.couldNotUpdate")),
  });

  const getStaffRoleForUser = (userId: string) => {
    const sa = (staffAssignments as StaffAssignment[]).find((a) => a.user_id === userId);
    return sa?.role_id ?? "";
  };

  const getRolesForUser = (userId: string): string[] => {
    return userRoles.filter((r) => r.user_id === userId && r.role !== "player").map((r) => r.role);
  };

  const noStaff = !isLoading && staffUserIds.length === 0;

  return (
    <div className="space-y-3">
      <Input placeholder={t("usersPage.searchPlaceholder")} value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="h-9" />

      {isLoading ? (
        <p className="text-sm text-muted-foreground text-center py-4">{t("common.loading")}</p>
      ) : noStaff || profiles.length === 0 ? (
        <div className="text-center py-12 space-y-2">
          <Users className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t("usersPage.noStaffYet")}</p>
          <p className="text-xs text-muted-foreground">{t("usersPage.assignRolesHint")}</p>
        </div>
      ) : (
        profiles.map((p) => (
          <Card key={p.id}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">{p.full_name || t("common.unknown")}</p>
                  <p className="text-xs text-muted-foreground">{p.phone || "–"} · {p.player_type || "–"}</p>
                </div>
                <div className="flex gap-1">
                  {getRolesForUser(p.id).map((role) => (
                    <Badge key={role} variant="secondary" className="text-[10px]">
                      {t(`usersPage.role${role.charAt(0).toUpperCase() + role.slice(1)}`)}
                    </Badge>
                  ))}
                  <Badge variant="outline">{t("common.level")} {p.level ?? 1}</Badge>
                </div>
              </div>
              <div className="flex gap-2">
                <Select value={String(p.level ?? 1)} onValueChange={(v) => {
                  const newLvl = parseInt(v);
                  if (newLvl !== (p.level ?? 1)) setLevelDialog({ id: p.id, name: p.full_name || "?", currentLevel: p.level ?? 1, newLevel: newLvl });
                }}>
                  <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder={t("common.level")} /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 10 }, (_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{t("common.level")} {i + 1}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-1 flex-wrap flex-1">
                  {STAFF_ROLE_OPTIONS.map((opt) => {
                    const hasRole = getRolesForUser(p.id).includes(opt.value);
                    return (
                      <Badge
                        key={opt.value}
                        variant={hasRole ? "default" : "outline"}
                        className={cn("cursor-pointer text-[10px] select-none transition-colors",
                          hasRole ? "" : "opacity-40 hover:opacity-70"
                        )}
                        onClick={() => toggleRole.mutate({ userId: p.id, role: opt.value, add: !hasRole })}
                      >
                        {t(opt.labelKey)}
                      </Badge>
                    );
                  })}
                </div>
                {getRolesForUser(p.id).includes("admin") && staffRoles.length > 0 && (
                  <Select value={getStaffRoleForUser(p.id)} onValueChange={(v) => assignStaffRole.mutate({ userId: p.id, roleId: v || null })}>
                    <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder={t("usersPage.staffRolePlaceholder")} /></SelectTrigger>
                    <SelectContent>
                      {(staffRoles as StaffRole[]).map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          <span className="flex items-center gap-1.5">
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: r.color }} />
                            {r.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {!noStaff && (
        <div className="flex justify-between">
          <button className="text-xs text-primary disabled:opacity-50" disabled={page === 0} onClick={() => setPage(page - 1)}>← {t("common.previous")}</button>
          <button className="text-xs text-primary disabled:opacity-50" disabled={profiles.length < pageSize} onClick={() => setPage(page + 1)}>{t("common.next")} →</button>
        </div>
      )}

      {/* Level change confirmation dialog */}
      <Dialog open={!!levelDialog} onOpenChange={(v) => { if (!v) { setLevelDialog(null); setLevelReason(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("usersPage.changeLevel.title", { name: levelDialog?.name })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-4">
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-1">{t("usersPage.changeLevel.from")}</p>
                <LevelBadge level={levelDialog?.currentLevel ?? 1} size="lg" />
              </div>
              <span className="text-lg text-muted-foreground">→</span>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-1">{t("usersPage.changeLevel.to")}</p>
                <LevelBadge level={levelDialog?.newLevel ?? 1} size="lg" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("usersPage.changeLevel.reason")}</Label>
              <Textarea
                value={levelReason}
                onChange={(e) => setLevelReason(e.target.value.slice(0, 200))}
                placeholder={t("usersPage.changeLevel.reasonPlaceholder")}
                rows={2}
                className="text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setLevelDialog(null); setLevelReason(""); }}>{t("common.cancel")}</Button>
            <Button
              onClick={() => levelDialog && updateLevel.mutate({ id: levelDialog.id, oldLevel: levelDialog.currentLevel, newLevel: levelDialog.newLevel, reason: levelReason })}
              disabled={updateLevel.isPending}
            >
              {updateLevel.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {t("usersPage.changeLevel.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
